import React, { FunctionComponent, useCallback, useRef, useState } from 'react';
import { FormProvider, ValidationRules, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { IAnnualFormProps } from './types';
import { useRequiredValidation } from '../../../../../utils/form/validation.utils';
import { FormRadioInput } from './RadioInput';
import { SelectInput } from '../../inputs/SelectInput';
import { FiPlusCircle, FiMinusCircle } from 'react-icons/fi';
import { FaLock } from 'react-icons/fa';
import { useEffect } from 'react';
import { AnnualFormDateTimeInput } from './DateTimeInput';
import { createAnnual, updateAnnual } from '../../../../../app/page/annuals/annuals.actions';
import { ICreateAnnualPayload } from '../../../../../app/page/annuals/annuals.types';
import { annualsSelectors } from '../../../../../app/resource/annuals/annuals.selectors';
import { useSelect } from '../../../../../utils/selector.utils';
import { IAnnual } from '../../../../../app/resource/annuals/annuals.types';
import { formatDateWithoutOrdinals } from '../../../../../utils/date.utils';

type FormValues = {
  year: { value: number; label: string };
  isCalendar: boolean;
  periods: { date: Date | undefined }[];
  formError?: string;
};

const currentYear = new Date().getFullYear();

export const AnnualAndPeriodsForm: FunctionComponent<IAnnualFormProps> = ({ annual }) => {
  const dispatch = useDispatch();
  const requiredValidation = useRequiredValidation();

  const [isCalendar, setIsCalendar] = useState<boolean>(annual ? annual.isCalendarYear === '1' : true);

  const existingAnnuals = useSelect(annualsSelectors.selectResourceList);
  const selectedYear = useRef<number>(annual ? +annual.year : currentYear);
  const previousYearAnnual = useRef<IAnnual>();
  const nextYearAnnual = useRef<IAnnual>();

  const changeYear = useCallback(
    (newYear: number) => {
      if (newYear) {
        selectedYear.current = newYear;
        previousYearAnnual.current = existingAnnuals.find((a) => +a.year === +newYear - 1);
        nextYearAnnual.current = existingAnnuals.find((a) => +a.year === newYear + 1);
      }
    },
    [previousYearAnnual, nextYearAnnual, existingAnnuals],
  );

  useEffect(() => {
    changeYear(annual ? +annual.year : currentYear);
  }, [changeYear, annual]);

  let defaultPeriods = [
    { date: new Date(currentYear, 0, 1) },
    { date: new Date(currentYear, 3, 1) },
    { date: new Date(currentYear, 6, 1) },
    { date: new Date(currentYear, 9, 1) },
    { date: new Date(currentYear, 11, 31) },
  ];

  if (annual?.quarters) {
    const result = annual.quarters.map((quarter) => {
      return { date: new Date(quarter.startDate) };
    });
    if (annual.quarters.length > 0) {
      result.push({ date: new Date(annual.quarters[annual.quarters.length - 1].endDate) });
    }
    defaultPeriods = result;
  }

  const formProps = useForm<FormValues>({
    defaultValues: {
      isCalendar: isCalendar,
      year: annual?.year ? { value: annual.year, label: annual.year.toString() } : undefined,
      periods: defaultPeriods,
    },
  });

  const { setValue, getValues, trigger, watch, control, errors, clearErrors, setError } = formProps;
  const { fields, insert, remove } = useFieldArray<{ date: Date | undefined }>({
    control,
    name: 'periods',
  });

  const yearChange = watch('year') as { value: number; label: string };

  /** It is executed when the user changes the year selection */
  useEffect(() => {
    if (yearChange && +yearChange.value !== selectedYear.current) {
      const newYear = +yearChange.value;
      const dif = newYear - selectedYear.current;
      for (let i = 0; i < fields.length; i++) {
        const date = getValues(`periods.${i}.date`) as Date;
        date.setFullYear(date.getFullYear() + dif);
        setValue(`periods.${i}.date`, date);
      }
      trigger(['periods']);
      changeYear(newYear);
    }
  }, [yearChange, getValues, trigger, selectedYear, setValue, fields.length, changeYear]);

  const isCalendarTuple = watch('isCalendar') as boolean;

  useEffect(() => {
    if (isCalendarTuple !== isCalendar) {
      setIsCalendar(!isCalendar);
    }
  }, [isCalendar, isCalendarTuple]);

  /** It is executed when the user changes the calendar type */
  useEffect(() => {
    if (isCalendarTuple !== isCalendar && isCalendarTuple === true) {
      setValue('periods.0.date', new Date(selectedYear.current, 0, 1));
      setValue(`periods.${fields.length - 1}.date`, new Date(selectedYear.current, 11, 31));
    }
  }, [isCalendar, setValue, selectedYear, fields.length, isCalendarTuple]);

  const onAdd = (index: number) => () => {
    insert(index + 1, { date: undefined });
  };

  const onRemove = (index: number) => () => {
    remove(index);
  };

  const calendarTypeOptions = [
    { id: 'custom_dates', value: false, label: 'Custom Dates' },
    { id: 'calendar_year', value: true, label: 'Calendar Year' },
  ];

  const yearsToDisplay = [currentYear, currentYear + 1, currentYear + 2];
  const availableYears = yearsToDisplay.filter((year) => !existingAnnuals.find((a) => +a.year === year));
  const yearOptions = availableYears.map((year) => ({ value: year.toString(), label: year.toString() }));
  const yearDefaultOption = yearOptions[0];

  const onSubmit = (formData: FormValues) => {
    const periods = formData.periods;

    const newPeriods = periods.filter((p): p is { date: Date } => p.date !== undefined);
    const isOrdered = newPeriods.every((date, i, arr) => i === 0 || arr[i - 1].date < date.date);

    if (!isOrdered) {
      setError(`formError`, {
        type: 'manual',
        message: 'Make sure there is no overlap between the periods',
      });
      return;
    }

    const quarters = annual?.quarters;
    const quartersToSubmit: { startDate: string; endDate: string; id?: string }[] = [];
    if (newPeriods.length >= 2) {
      for (let i = 0; i < newPeriods.length - 1; i++) {
        let a: { startDate: string; endDate: string; id?: string } = {
          startDate: newPeriods[i].date.toISOString(),
          endDate:
            i === newPeriods.length - 2
              ? newPeriods[i + 1].date.toISOString()
              : new Date(new Date(newPeriods[i + 1].date).setDate(newPeriods[i + 1].date.getDate() - 1)).toISOString(),
        };
        const quarter = quarters?.find((q) => q.number == i + 1);
        a = { ...a, id: quarter?.id };

        quartersToSubmit.push(a);
      }
    }

    if (previousYearAnnual.current) {
      const quarters = previousYearAnnual.current.quarters;
      const previousAnnualLastQuarter = quarters[quarters.length - 1];
      const previousAnnualLastQuarterEndDate = new Date(previousAnnualLastQuarter.endDate);
      if (previousAnnualLastQuarter && previousAnnualLastQuarterEndDate >= newPeriods[0].date) {
        setError(`formError`, {
          type: 'manual',
          message: `The start date overlaps with the last period of the previous annual. Please choose a start date after ${formatDateWithoutOrdinals(
            previousAnnualLastQuarter.endDate,
          )}`,
        });
        return;
      }
    }

    if (nextYearAnnual.current) {
      const quarters = nextYearAnnual.current.quarters;
      const nextAnnualFirstQuarter = quarters[0];
      const startDate = new Date(nextAnnualFirstQuarter.startDate);
      if (nextAnnualFirstQuarter && startDate <= newPeriods[newPeriods.length - 1].date) {
        setError(`formError`, {
          type: 'manual',
          message: `The end date overlaps with the first period of the next annual. Please choose an end date before ${formatDateWithoutOrdinals(
            nextAnnualFirstQuarter.endDate,
          )}`,
        });
        return;
      }
    }

    const payload: ICreateAnnualPayload = {
      annual: {
        year: formData.year ? formData.year.value : selectedYear.current,
        isCalendarYear: formData.isCalendar ? '1' : '0',
        startDate: new Date(newPeriods[0].date).toISOString(),
        endDate: new Date(newPeriods[newPeriods.length - 1].date).toISOString(),
      },
      quarters: quartersToSubmit,
    };

    if (annual) {
      dispatch(updateAnnual(annual.id, payload));
    } else {
      dispatch(createAnnual(payload));
    }
  };

  /**
   *  Clears the general form error if exists before executing the submit.
   *  General errors are not assigned to a specific field so they won't disappear upon onChange events in the fields.
   *  Regular react hook form submission wouldn't work if a general error exists in the form,
   *  so we have to clean them first
   */
  const onSubmitWrapper = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearErrors('formError');
    formProps.handleSubmit(onSubmit)();
  };

  const isDisabled = (index: number, date?: Date) => {
    return Boolean((isCalendar && (index === 0 || index === fields.length - 1)) || (date && date < new Date()));
  };

  return (
    <FormProvider {...formProps}>
      {errors.formError && <p className="f-2 red">{errors.formError.message}</p>}
      <form id="annuals-form" className="topic-form" onSubmit={onSubmitWrapper} method="post">
        <div className="field-container flex justify-start mt-2 themed-radio">
          <FormRadioInput id="type" name="isCalendar" options={calendarTypeOptions} />
        </div>
        <div className="field-container">
          {annual ? (
            <>
              <label htmlFor="year">Year</label>
              <input id="year" name="year" type="text" placeholder={annual.year.toString()} disabled />
            </>
          ) : (
            <SelectInput
              id="year"
              name="year"
              label="Year"
              validation={requiredValidation}
              options={yearOptions}
              defaultOptions={yearDefaultOption}
              isSingle
            />
          )}
        </div>
        {fields.map(
          (field, index) =>
            field && (
              <div className="field-container flex align-center justify-between" key={field.id}>
                <DateInput
                  requiredValidation={requiredValidation}
                  index={index}
                  value={field.date}
                  disabled={isDisabled(index, annual ? field.date : undefined)}
                  fieldsNumber={fields.length}
                />
                {isDisabled(index, annual ? field.date : undefined) && (
                  <FaLock size={18} style={{ position: 'absolute', top: 38, right: 155 }} />
                )}
                <Buttons
                  onAdd={onAdd(index)}
                  onRemove={onRemove(index)}
                  currentIndex={index}
                  currentDateEnabled={!isDisabled(index, annual ? field.date : undefined)}
                  nextDateEnabled={!isDisabled(index + 1, annual ? fields[index + 1]?.date : undefined)}
                  numberOfFields={fields.length}
                  lastIndex={fields.length - 1}
                />
              </div>
            ),
        )}
      </form>
    </FormProvider>
  );
};

interface IDateProps {
  requiredValidation: ValidationRules;
  index: number;
  value?: Date;
  disabled: boolean;
  fieldsNumber: number;
  minDate?: Date;
  maxDate?: Date;
}

const DateInput: FunctionComponent<IDateProps> = ({
  requiredValidation,
  index,
  value,
  disabled,
  fieldsNumber,
  minDate,
  maxDate,
}) => {
  let label = '';
  switch (index) {
    case 0:
      label = 'P1 Start / Start of year';
      break;
    case fieldsNumber - 1:
      label = `P${fieldsNumber - 1} End / End of year`;
      break;
    default:
      label = `P${index + 1} Start`;
  }

  return (
    <div id="2">
      <AnnualFormDateTimeInput
        id={`periods.${index}.date`}
        name={`periods.${index}.date`}
        label={label}
        value={value instanceof Date && !isNaN(value.getTime()) ? value.toISOString() : undefined}
        defaultValue={value}
        minDate={minDate}
        maxDate={maxDate}
        placeholder="Enter Start Date"
        validation={requiredValidation}
        disabled={disabled}
        selected={value}
      />
    </div>
  );
};

interface IButtonsProps {
  onAdd?: () => void;
  onRemove?: () => void;
  currentIndex: number;
  lastIndex: number;
  currentDateEnabled: boolean;
  nextDateEnabled: boolean;
  numberOfFields: number;
  date?: Date;
}
const Buttons: FunctionComponent<IButtonsProps> = ({
  onAdd,
  onRemove,
  currentIndex,
  lastIndex,
  currentDateEnabled,
  nextDateEnabled,
  numberOfFields,
}) => {
  const addButton = <FiPlusCircle className="pointer" size={30} onClick={onAdd} />;
  const addRemoveButton = (
    <>
      <FiMinusCircle className="pointer" size={30} color="red" onClick={onRemove} />
      <FiPlusCircle className="pointer" size={30} onClick={onAdd} />
    </>
  );

  const renderButton = () => {
    if (currentIndex === 0) {
      return nextDateEnabled || numberOfFields === 2 ? addButton : undefined;
    }

    if (currentIndex === lastIndex) {
      return undefined;
    }

    if (currentDateEnabled) {
      return addRemoveButton;
    }

    return nextDateEnabled || currentIndex === lastIndex - 1 ? addButton : undefined;
  };

  return <div className="buttons mt-2">{renderButton()}</div>;
};
